'use client';

import React from 'react';
import clsx from 'clsx';
import type { MultistepFormProps } from '../types';
import type { UseMultistepFormStateResult } from '../hooks/useMultistepFormState';
import { HeadlineWrapper } from '@/components/headline-wrapper/HeadlineWrapper';
import FormStep from '@/sections/forms/multistep-form/steps/FormStep/FormStep';
import TopicsStep from '@/sections/forms/multistep-form/steps/TopicsStep/TopicsStep';
import ConsultationStep from '@/sections/forms/multistep-form/steps/ConsultationStep/ConsultationStep';
import SubmittingState from '@/sections/forms/multistep-form/steps/StepSubmitting/StepSubmitting';
import SuccessState from '@/sections/forms/multistep-form/steps/StepSuccess/StepSuccess';
import ErrorState from '@/sections/forms/multistep-form/steps/StepError/StepError';

type StepRendererProps = {
  formState: UseMultistepFormStateResult;
  formHeadline?: MultistepFormProps['formHeadline'];
  form: MultistepFormProps['form'];
  hiddenFields?: MultistepFormProps['hiddenFields'];
  successModalTitle?: MultistepFormProps['successModalTitle'];
  successModalDescription?: MultistepFormProps['successModalDescription'];
};

const resolveActiveFormHeadline = (
  formHeadline: MultistepFormProps['formHeadline'],
  currentStep: number,
) => {
  if (!formHeadline) return undefined;
  if (!Array.isArray(formHeadline)) return formHeadline;

  const sanitizedFormHeadlines = formHeadline.filter(Boolean);
  if (sanitizedFormHeadlines.length === 0) return undefined;
  if (sanitizedFormHeadlines.length === 1) return sanitizedFormHeadlines[0];

  return sanitizedFormHeadlines[currentStep - 1];
};

const StepRenderer: React.FC<StepRendererProps> = ({
  formState,
  formHeadline,
  form,
  hiddenFields,
  successModalTitle,
  successModalDescription,
}) => {
  if (formState.isSuccess) {
    return (
      <SuccessState
        onClose={formState.resetAll}
        modalTitle={successModalTitle}
        modalDescription={successModalDescription}
      />
    );
  }
  if (formState.isError) {
    return <ErrorState onClose={formState.resetAll} />;
  }
  if (formState.isFinalStepSubmitting) return <SubmittingState />;

  const stepNode = (() => {
    switch (formState.currentStep) {
      case 1:
        return (
          <ConsultationStep
            loading={formState.loadingConsultations}
            consultations={formState.consultations}
            selectedTitle={formState.selectedConsultationTitle}
            onSelect={formState.setSelectedConsultationTitle}
          />
        );

      case 2:
        return (
          <TopicsStep
            topics={formState.topics}
            selectedTopics={formState.selectedTopics}
            onToggleTopic={formState.onToggleTopic}
            anliegen={formState.anliegen}
            onChangeAnliegen={formState.setAnliegen}
            hasSelectedConsultation={Boolean(formState.selectedConsultation)}
          />
        );

      case 3:
        return (
          <FormStep
            form={form}
            hiddenFields={hiddenFields}
            multistepConsultationTitle={formState.selectedConsultationTitle}
            multistepTopics={formState.selectedTopics}
            multistepAnliegen={formState.anliegen}
            onSubmitRef={(submit) => {
              formState.registerFinalSubmit(submit);
            }}
            onSubmitStart={formState.onSubmitStart}
            onSuccess={formState.onSubmitSuccess}
            onError={formState.onSubmitError}
            presetCallbackRequestNotes={formState.presetCallbackRequestNotes}
            presetWebsiteSelectedTopics={formState.presetWebsiteSelectedTopics}
          />
        );

      default:
        return null;
    }
  })();
  const activeFormHeadline = resolveActiveFormHeadline(
    formHeadline,
    formState.currentStep,
  );
  const activeFormHeadlineClassName = activeFormHeadline?.className;

  return (
    <>
      {activeFormHeadline ? (
        <HeadlineWrapper
          {...activeFormHeadline}
          className={clsx('!px-0', activeFormHeadlineClassName)}
        />
      ) : null}

      <div className={activeFormHeadline ? 'my-8' : 'mt-2'}>{stepNode}</div>
    </>
  );
};

export default StepRenderer;
